import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  BookOpen,
  Calculator,
  Check,
  GraduationCap,
  Info,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Save,
  Search,
  Shield,
  ShieldCheck,
  UserCheck,
  Users,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';

import SettingsModal from '../components/SettingsModal';
import SettingsPageHeader from '../components/SettingsPageHeader';
import ToastMessage from '../components/ToastMessage';

import { useToast } from '../hooks/useToast';

import { rbacSettingsApi } from '../services/rbacSettingsApi';

import {
  getApiErrorMessage,
} from '../services/settingsResponse';

const inputClass =
  'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500';

const normalizeModuleName = (value) => {
  if (!value) {
    return 'Other';
  }

  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
};

const setsAreEqual = (firstSet, secondSet) => {
  if (firstSet.size !== secondSet.size) {
    return false;
  }

  return [...firstSet].every((value) =>
    secondSet.has(value),
  );
};

const getRoleIcon = (roleName) => {
  const name = String(roleName || '').toLowerCase();
  if (name.includes('accountant') || name.includes('finance')) return Calculator;
  if (name.includes('librarian') || name.includes('library')) return BookOpen;
  if (name.includes('parent') || name.includes('guardian')) return Users;
  if (name.includes('teacher') || name.includes('faculty')) return GraduationCap;
  if (name.includes('principal') || name.includes('admin')) return Shield;
  return UserCheck; // Default for other custom roles
};

const RolePermissionSettingsPage = () => {
  const { schoolId } = useSchool();

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] =
    useState([]);

  const [selectedRoleId, setSelectedRoleId] =
    useState('');

  const [
    selectedPermissionIds,
    setSelectedPermissionIds,
  ] = useState(new Set());

  const [
    originalPermissionIds,
    setOriginalPermissionIds,
  ] = useState(new Set());

  const [loading, setLoading] = useState(true);

  const [
    permissionLoading,
    setPermissionLoading,
  ] = useState(false);

  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] =
    useState(false);

  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
  });

  const [creating, setCreating] =
    useState(false);

  const {
    toast,
    showToast,
    clearToast,
  } = useToast();

  const selectedRole = useMemo(
    () =>
      roles.find(
        (role) => role.id === selectedRoleId,
      ) || null,
    [roles, selectedRoleId],
  );

  const hasChanges = useMemo(
    () =>
      !setsAreEqual(
        selectedPermissionIds,
        originalPermissionIds,
      ),
    [
      selectedPermissionIds,
      originalPermissionIds,
    ],
  );

  const loadBaseData = useCallback(async () => {
    if (!schoolId) {
      setRoles([]);
      setPermissions([]);
      setSelectedRoleId('');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [
        roleList,
        permissionList,
      ] = await Promise.all([
        rbacSettingsApi.listRoles(schoolId),
        rbacSettingsApi.listPermissions(schoolId),
      ]);

      setRoles(roleList || []);
      setPermissions(permissionList || []);

      setSelectedRoleId((currentRoleId) => {
        const currentStillExists = roleList.some(
          (role) => role.id === currentRoleId,
        );

        if (currentStillExists) {
          return currentRoleId;
        }

        return roleList[0]?.id || '';
      });
    } catch (error) {
      showToast(
        getApiErrorMessage(
          error,
          'Unable to load roles and permissions.',
        ),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [schoolId, showToast]);

  const loadRolePermissions = useCallback(
    async (roleId) => {
      if (!schoolId || !roleId) {
        setSelectedPermissionIds(new Set());
        setOriginalPermissionIds(new Set());
        return;
      }

      setPermissionLoading(true);

      try {
        const assignedPermissions =
          await rbacSettingsApi.listRolePermissions(
            schoolId,
            roleId,
          );

        const assignedIds = new Set(
          (assignedPermissions || [])
            .map((permission) => permission.id)
            .filter(Boolean),
        );

        setSelectedPermissionIds(
          new Set(assignedIds),
        );

        setOriginalPermissionIds(
          new Set(assignedIds),
        );
      } catch (error) {
        setSelectedPermissionIds(new Set());
        setOriginalPermissionIds(new Set());

        showToast(
          getApiErrorMessage(
            error,
            'Unable to load assigned permissions.',
          ),
          'error',
        );
      } finally {
        setPermissionLoading(false);
      }
    },
    [schoolId, showToast],
  );

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    loadRolePermissions(selectedRoleId);
  }, [
    selectedRoleId,
    loadRolePermissions,
  ]);

  const filteredPermissions = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return permissions;
    }

    return permissions.filter((permission) => {
      const searchableText = [
        permission.name,
        permission.code,
        permission.module,
        permission.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(
        normalizedSearch,
      );
    });
  }, [permissions, search]);

  const groupedPermissions = useMemo(() => {
    const groups = filteredPermissions.reduce(
      (result, permission) => {
        const moduleName =
          normalizeModuleName(permission.module);

        if (!result[moduleName]) {
          result[moduleName] = [];
        }

        result[moduleName].push(permission);

        return result;
      },
      {},
    );

    return Object.entries(groups)
      .sort(([firstModule], [secondModule]) =>
        firstModule.localeCompare(secondModule),
      )
      .map(([moduleName, modulePermissions]) => ({
        moduleName,
        permissions: modulePermissions.sort(
          (firstPermission, secondPermission) =>
            String(
              firstPermission.name ||
                firstPermission.code,
            ).localeCompare(
              String(
                secondPermission.name ||
                  secondPermission.code,
              ),
            ),
        ),
      }));
  }, [filteredPermissions]);

  const togglePermission = (permissionId) => {
    setSelectedPermissionIds(
      (currentPermissionIds) => {
        const nextPermissionIds = new Set(
          currentPermissionIds,
        );

        if (nextPermissionIds.has(permissionId)) {
          nextPermissionIds.delete(permissionId);
        } else {
          nextPermissionIds.add(permissionId);
        }

        return nextPermissionIds;
      },
    );
  };

  const selectAllVisible = () => {
    setSelectedPermissionIds(
      (currentPermissionIds) => {
        const nextPermissionIds = new Set(
          currentPermissionIds,
        );

        filteredPermissions.forEach(
          (permission) => {
            nextPermissionIds.add(permission.id);
          },
        );

        return nextPermissionIds;
      },
    );
  };

  const clearVisible = () => {
    setSelectedPermissionIds(
      (currentPermissionIds) => {
        const nextPermissionIds = new Set(
          currentPermissionIds,
        );

        filteredPermissions.forEach(
          (permission) => {
            nextPermissionIds.delete(permission.id);
          },
        );

        return nextPermissionIds;
      },
    );
  };

  const resetChanges = () => {
    setSelectedPermissionIds(
      new Set(originalPermissionIds),
    );
  };

  const handleSavePermissions = async () => {
    if (!schoolId || !selectedRoleId) {
      showToast(
        'Select a role before saving permissions.',
        'error',
      );

      return;
    }

    const permissionIds = Array.from(
      selectedPermissionIds,
    );

    if (permissionIds.length === 0) {
      showToast(
        'At least one permission must remain assigned to the role.',
        'error',
      );

      return;
    }

    setSaving(true);

    try {
      await rbacSettingsApi.replaceRolePermissions(
        schoolId,
        selectedRoleId,
        permissionIds,
      );

      setOriginalPermissionIds(
        new Set(permissionIds),
      );

      showToast(
        `Permissions updated for ${
          selectedRole?.name || 'the selected role'
        }.`,
      );
    } catch (error) {
      showToast(
        getApiErrorMessage(
          error,
          'Unable to update role permissions.',
        ),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async (event) => {
    event.preventDefault();

    const roleName = createForm.name.trim();

    if (!roleName) {
      showToast(
        'Role name is required.',
        'error',
      );

      return;
    }

    setCreating(true);

    try {
      const createdRole =
        await rbacSettingsApi.createRole(
          schoolId,
          {
            name: roleName,
            description:
              createForm.description.trim(),
          },
        );

      setCreateOpen(false);

      setCreateForm({
        name: '',
        description: '',
      });

      const updatedRoles =
        await rbacSettingsApi.listRoles(schoolId);

      setRoles(updatedRoles || []);

      if (createdRole?.id) {
        setSelectedRoleId(createdRole.id);
      } else if (updatedRoles?.length > 0) {
        const matchingRole = updatedRoles.find(
          (role) =>
            role.name
              ?.trim()
              .toLowerCase() ===
            roleName.toLowerCase(),
        );

        setSelectedRoleId(
          matchingRole?.id ||
            updatedRoles[0]?.id ||
            '',
        );
      }

      showToast('Role created successfully.');
    } catch (error) {
      showToast(
        getApiErrorMessage(
          error,
          'Unable to create role.',
        ),
        'error',
      );
    } finally {
      setCreating(false);
    }
  };

  const assignedCount =
    selectedPermissionIds.size;

  if (loading) {
    return (
      <div className="flex min-h-[55vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />

          <p className="mt-3 text-sm text-gray-500">
            Loading roles and permissions...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastMessage
        toast={toast}
        onClose={clearToast}
      />

      <SettingsPageHeader
        title="Roles & Permissions"
        description="Create roles and control which ERP actions each role can perform."
        action={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Create role
          </button>
        }
      />

      <div className="mb-5 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />

        <div className="text-sm leading-6 text-blue-800">
          <p className="font-semibold">
            Permission changes replace the role’s complete
            permission set.
          </p>

          <p>
            Check a permission to assign it. Uncheck a
            permission to remove it. The backend currently
            requires at least one permission for each saved
            role.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
        {/* Roles */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">
                  School roles
                </h2>

                <p className="mt-1 text-xs text-gray-500">
                  {roles.length} active role
                  {roles.length === 1 ? '' : 's'}
                </p>
              </div>

              <button
                type="button"
                onClick={loadBaseData}
                className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Refresh roles"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[650px] space-y-2 overflow-y-auto p-3">
            {roles.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <ShieldCheck className="mx-auto h-9 w-9 text-gray-300" />

                <p className="mt-3 text-sm font-medium text-gray-700">
                  No roles found
                </p>

                <p className="mt-1 text-xs text-gray-500">
                  Create a role to start assigning
                  permissions.
                </p>
              </div>
            ) : (
              roles.map((role) => {
                const selected =
                  role.id === selectedRoleId;

                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() =>
                      setSelectedRoleId(role.id)
                    }
                    className={`w-full rounded-xl border p-4 text-left transition ${
                      selected
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`rounded-lg p-2 ${
                          selected
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {(() => {
                          const Icon = getRoleIcon(role.name);
                          return <Icon className="h-4 w-4" />;
                        })()}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p
                            className={`truncate text-sm font-semibold ${
                              selected
                                ? 'text-blue-900'
                                : 'text-gray-900'
                            }`}
                          >
                            {role.name}
                          </p>

                          {selected && (
                            <Check className="h-4 w-4 shrink-0 text-blue-600" />
                          )}
                        </div>

                        <p className="mt-1 truncate text-xs text-gray-500">
                          {role.description ||
                            role.slug ||
                            'No description'}
                        </p>

                        {role.is_system_role && (
                          <span className="mt-2 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                            System role
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {/* Permissions */}
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-gray-900">
                    {selectedRole
                      ? `${selectedRole.name} permissions`
                      : 'Role permissions'}
                  </h2>

                  {hasChanges && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                      Unsaved changes
                    </span>
                  )}
                </div>

                <p className="mt-1 text-xs text-gray-500">
                  {assignedCount} of {permissions.length}{' '}
                  permissions selected
                </p>
              </div>

              <div className="relative w-full lg:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <input
                  type="search"
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search permissions..."
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={selectAllVisible}
                disabled={
                  !selectedRoleId ||
                  filteredPermissions.length === 0
                }
                className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Select all visible
              </button>

              <button
                type="button"
                onClick={clearVisible}
                disabled={
                  !selectedRoleId ||
                  filteredPermissions.length === 0
                }
                className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear visible
              </button>

              <button
                type="button"
                onClick={resetChanges}
                disabled={!hasChanges}
                className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Reset changes
              </button>
            </div>
          </div>

          <div className="min-h-[420px] p-5">
            {!selectedRoleId ? (
              <div className="flex min-h-[380px] flex-col items-center justify-center text-center">
                <ShieldCheck className="h-12 w-12 text-gray-300" />

                <p className="mt-4 font-semibold text-gray-700">
                  Select a role
                </p>

                <p className="mt-1 text-sm text-gray-500">
                  Choose a role from the left to manage its
                  permissions.
                </p>
              </div>
            ) : permissionLoading ? (
              <div className="flex min-h-[380px] flex-col items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />

                <p className="mt-3 text-sm text-gray-500">
                  Loading role permissions...
                </p>
              </div>
            ) : groupedPermissions.length === 0 ? (
              <div className="flex min-h-[380px] flex-col items-center justify-center text-center">
                <Search className="h-10 w-10 text-gray-300" />

                <p className="mt-3 font-semibold text-gray-700">
                  No permissions found
                </p>

                <p className="mt-1 text-sm text-gray-500">
                  Change your search and try again.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedPermissions.map(
                  ({
                    moduleName,
                    permissions:
                      modulePermissions,
                  }) => (
                    <div key={moduleName}>
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
                          {moduleName}
                        </h3>

                        <span className="text-xs text-gray-400">
                          {
                            modulePermissions.filter(
                              (permission) =>
                                selectedPermissionIds.has(
                                  permission.id,
                                ),
                            ).length
                          }
                          /{modulePermissions.length}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {modulePermissions.map(
                          (permission) => {
                            const checked =
                              selectedPermissionIds.has(
                                permission.id,
                              );

                            return (
                              <label
                                key={permission.id}
                                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                                  checked
                                    ? 'border-blue-300 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    togglePermission(
                                      permission.id,
                                    )
                                  }
                                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />

                                <div className="min-w-0 flex-1">
                                  <p
                                    className={`text-sm font-semibold ${
                                      checked
                                        ? 'text-blue-900'
                                        : 'text-gray-900'
                                    }`}
                                  >
                                    {permission.name ||
                                      permission.code}
                                  </p>

                                  <p className="mt-1 break-all font-mono text-[11px] text-gray-500">
                                    {permission.code}
                                  </p>

                                  {permission.description && (
                                    <p className="mt-2 text-xs leading-5 text-gray-500">
                                      {
                                        permission.description
                                      }
                                    </p>
                                  )}
                                </div>
                              </label>
                            );
                          },
                        )}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              Saving replaces all permissions currently
              assigned to this role.
            </p>

            <button
              type="button"
              onClick={handleSavePermissions}
              disabled={
                !selectedRoleId ||
                !hasChanges ||
                saving ||
                permissionLoading ||
                selectedPermissionIds.size === 0
              }
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}

              {saving
                ? 'Saving permissions...'
                : 'Save permissions'}
            </button>
          </div>
        </section>
      </div>

      <SettingsModal
        open={createOpen}
        title="Create role"
        description="Create a school role and assign permissions after it is created."
        onClose={
          creating
            ? () => {}
            : () => setCreateOpen(false)
        }
        maxWidth="max-w-lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              form="create-role-form"
              disabled={
                creating ||
                !createForm.name.trim()
              }
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {creating && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}

              Create role
            </button>
          </div>
        }
      >
        <form
          id="create-role-form"
          onSubmit={handleCreateRole}
          className="space-y-5"
        >
          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Role name *
            </span>

            <input
              type="text"
              required
              maxLength={100}
              value={createForm.name}
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              placeholder="Example: Accountant"
              className={inputClass}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Description
            </span>

            <textarea
              rows={4}
              value={createForm.description}
              onChange={(event) =>
                setCreateForm((current) => ({
                  ...current,
                  description:
                    event.target.value,
                }))
              }
              placeholder="Describe what this role is responsible for."
              className={inputClass}
            />
          </label>
        </form>
      </SettingsModal>
    </>
  );
};

export default RolePermissionSettingsPage;